import { useEffect, useRef, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

// Lifted byte-for-byte from src/components/SchedulePreview.tsx (formerly inline)
// so SchedulePreview's day picker and TodayTab's reschedule picker render
// identical labels. Do NOT diverge — formatDate parity is non-negotiable.
function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export interface DayChipProps {
  currentDate: string;
  isOpen: boolean;
  onToggle: () => void;
  onPick: (iso: string) => void;
  /** When false, suppresses the periwinkle trigger pill and renders only
   *  the expanded 14-day row inline (no absolute positioning). Use when
   *  the parent owns the interaction surface (e.g. TodayTab's Pencil
   *  reschedule button). Defaults to true so SchedulePreview's behavior
   *  stays identical. */
  showTriggerPill?: boolean;
  /** Slot rendered after the 14-day row inside the same pill-less wrapper.
   *  Used by TodayTab's reschedule popover to inject the time picker and
   *  Save button so a single wrapper owns one outside-click handler. Only
   *  rendered when showTriggerPill === false. */
  children?: ReactNode;
}

/** Inline 14-day picker chip for fixing a misread date on an event row.
 *  Visual style mirrors the day pill from UpdateConfirmationChip (periwinkle
 *  tint, rounded, periwinkle text). Expanded state shows a horizontal-scroll
 *  row of 14 day pills (today + next 13). The selected pill auto-scrolls into
 *  view (centered) when the picker opens, so users can see the highlighted
 *  "wrong" date alongside the correct one and fix in one tap. */
export const DayChip = ({ currentDate, isOpen, onToggle, onPick, showTriggerPill = true, children }: DayChipProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const selectedPillRef = useRef<HTMLButtonElement>(null);

  // Build 14 day options starting from today (local).
  const dayOpts = (() => {
    const today = new Date();
    const opts: Array<{ iso: string; topLabel: string; dayNum: number; isToday: boolean }> = [];
    const wkAbbrev = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    for (let i = 0; i < 14; i += 1) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      opts.push({
        iso,
        topLabel: i === 0 ? "Today" : wkAbbrev[d.getDay()],
        dayNum: d.getDate(),
        isToday: i === 0,
      });
    }
    return opts;
  })();

  // Outside-click closes the picker.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        onToggle();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, onToggle]);

  // Auto-scroll selected pill into view when picker opens.
  useEffect(() => {
    if (isOpen && selectedPillRef.current) {
      selectedPillRef.current.scrollIntoView({
        behavior: "auto",
        block: "nearest",
        inline: "center",
      });
    }
  }, [isOpen]);

  // Pill-less mode: parent owns the interaction surface and placement.
  // Renders only the 14-day row inline. Outside-click still calls onToggle
  // so the parent can close. Parent gates mounting via isOpen at the call
  // site, so this branch does not gate on isOpen here.
  if (!showTriggerPill) {
    return (
      <div
        ref={wrapperRef}
        style={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E0DEEE",
          borderRadius: 12,
          boxShadow: "0 6px 20px rgba(28, 26, 62, 0.12)",
          padding: 8,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          maxWidth: "min(360px, 80vw)",
        }}
      >
        <div style={{ display: "flex", gap: 6, overflowX: "auto", scrollbarWidth: "thin" }}>
          {dayOpts.map((opt) => {
            const selected = opt.iso === currentDate;
            return (
              <button
                key={opt.iso}
                ref={selected ? selectedPillRef : undefined}
                type="button"
                onClick={() => onPick(opt.iso)}
                className="font-body"
                style={{
                  backgroundColor: selected ? "#5050C8" : "#E8E8F4",
                  color: selected ? "#FFFFFF" : "#1C1A3E",
                  padding: "6px 10px",
                  borderRadius: 10,
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: 44,
                  flexShrink: 0,
                  lineHeight: 1.1,
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.85 }}>{opt.topLabel}</span>
                <span style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>{opt.dayNum}</span>
              </button>
            );
          })}
        </div>
        {children}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={onToggle}
        className="font-body"
        style={{
          backgroundColor: "#E8E8F4",
          color: "#5050C8",
          padding: "3px 8px",
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          border: "1px solid #C8C8F4",
          cursor: "pointer",
          whiteSpace: "nowrap",
          lineHeight: 1.3,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {formatDate(currentDate)} <ChevronDown size={12} strokeWidth={2.5} />
      </button>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 30,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E0DEEE",
            borderRadius: 12,
            boxShadow: "0 6px 20px rgba(28, 26, 62, 0.12)",
            padding: 8,
            display: "flex",
            gap: 6,
            overflowX: "auto",
            maxWidth: "min(360px, 80vw)",
            scrollbarWidth: "thin",
          }}
        >
          {dayOpts.map((opt) => {
            const selected = opt.iso === currentDate;
            return (
              <button
                key={opt.iso}
                ref={selected ? selectedPillRef : undefined}
                type="button"
                onClick={() => onPick(opt.iso)}
                className="font-body"
                style={{
                  backgroundColor: selected ? "#5050C8" : "#E8E8F4",
                  color: selected ? "#FFFFFF" : "#1C1A3E",
                  padding: "6px 10px",
                  borderRadius: 10,
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: 44,
                  flexShrink: 0,
                  lineHeight: 1.1,
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.85 }}>{opt.topLabel}</span>
                <span style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>{opt.dayNum}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DayChip;
